import { useEffect, useState } from "react";

const STORAGE_KEY = "exit_intent_dismissed_until";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const INITIAL_DELAY_MS = 3000;

function isDismissed(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    const until = parseInt(v, 10);
    if (Number.isNaN(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
}

export function dismissExitIntent() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + SEVEN_DAYS_MS));
  } catch {
    /* ignore */
  }
}

export function useExitIntent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isDismissed()) return;

    let armed = false;
    let triggered = false;
    let lastScrollY = window.scrollY;
    let lastScrollT = Date.now();

    const armTimer = window.setTimeout(() => {
      armed = true;
    }, INITIAL_DELAY_MS);

    const trigger = () => {
      if (!armed || triggered) return;
      if (isDismissed()) return;
      triggered = true;
      setOpen(true);
    };

    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) trigger();
    };

    const onScroll = () => {
      const now = Date.now();
      const dy = window.scrollY - lastScrollY;
      const dt = now - lastScrollT;
      // fast scroll up near top
      if (dy < -40 && dt < 250 && window.scrollY < 400) {
        trigger();
      }
      lastScrollY = window.scrollY;
      lastScrollT = now;
    };

    // Push a history state so back button fires popstate without leaving
    let pushed = false;
    try {
      window.history.pushState({ exitIntent: true }, "");
      pushed = true;
    } catch {
      /* ignore */
    }
    const onPopState = () => {
      if (!armed || triggered) return;
      trigger();
      // re-push so subsequent back still works
      try {
        window.history.pushState({ exitIntent: true }, "");
      } catch {
        /* ignore */
      }
    };

    document.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("popstate", onPopState);

    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("popstate", onPopState);
      if (pushed) {
        try {
          // Best-effort cleanup of the dummy history entry
          if (window.history.state && (window.history.state as any).exitIntent) {
            window.history.back();
          }
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return { open, setOpen };
}
